import React from 'react';
import { Badge } from 'reactstrap';
import { parseHtml } from 'helpers/index';
import { QUESTION_TYPE, RESULT_STATUS, UNIT_STATUS } from 'constants/constants';
import {
  DragAndDrop,
  FillInTheBlank,
  FillTheGap,
  FreeText,
  MultipleChoice,
  Ordering,
} from 'modules/Course/Unit/components/QuestionTypev2';

/** Same palette family as `ModalDetail.js` (Report/Tests). */
export const ADAPTIVE_COLOR_STATUS = {
  PENDING: 'warning',
  COMPLETED: 'success',
  FAILED: 'danger',
  PASSED: 'success',
  NOT_ATTEMPT: 'secondary',
};

/**
 * Normalize adaptive API payloads so report result components receive expected shapes.
 * @param {Record<string, any>} item
 */
export function normalizeAdaptiveResult(item) {
  if (!item) return item;
  const next = { ...item };

  if (item.type === QUESTION_TYPE.DRAGDROP) {
    const hasAnswers =
      Array.isArray(next.resultAnswers) && next.resultAnswers.length > 0;
    const raw = next.result;
    if (!hasAnswers && Array.isArray(raw) && raw.length > 0) {
      next.resultAnswers = raw.map((content, i) => ({
        content: typeof content === 'string' ? content : String(content),
        correct:
          Array.isArray(item.resultAnswers) && item.resultAnswers[i]
            ? item.resultAnswers[i].correct
            : undefined,
      }));
    }
  }

  return next;
}

function noop() {}

/** @param {Record<string, any>} normalized */
function buildOrderingValueForReadOnly(normalized) {
  if (Array.isArray(normalized?.result) && normalized.result.length > 0) {
    return normalized.result.map((item, i) => ({
      id: `ord-${i}`,
      content: item,
    }));
  }
  if (
    Array.isArray(normalized?.resultAnswers) &&
    normalized.resultAnswers.length > 0
  ) {
    return normalized.resultAnswers.map((item, i) => ({
      id: item?._id ?? item?.id ?? `ord-${i}`,
      content: item?.content ?? item,
    }));
  }
  return [];
}

/** @param {Record<string, any>} normalized */
function buildDragDropValueForReadOnly(normalized) {
  const answers =
    Array.isArray(normalized?.resultAnswers) && normalized.resultAnswers.length
      ? normalized.resultAnswers
      : Array.isArray(normalized?.result)
      ? normalized.result.map((c) =>
          typeof c === 'string'
            ? { content: c }
            : { content: c?.content ?? String(c) },
        )
      : [];
  return answers.map((item, i) => ({
    id: item?._id ?? item?.id ?? `dd-${i}`,
    content: typeof item === 'string' ? item : item?.content ?? '',
  }));
}

/** @param {Record<string, any>} normalized */
function buildDragDropQuestionsForReadOnly(normalized) {
  return normalized?.data?.map((d) => d?.content) ?? [];
}

/** MTC: boolean[] aligned to `data` options. */
function buildMtcCorrectValue(normalized) {
  const data = normalized?.data;
  if (!Array.isArray(data)) return [];
  return data.map((d) => d?.correct === true);
}

/** Ordering: correct order from `data` (strings or `{ content }`). */
function buildOrderingCorrectValue(normalized) {
  const data = normalized?.data;
  if (!Array.isArray(data) || data.length === 0) return [];
  return data.map((item, i) => ({
    id: `ord-cor-${i}`,
    content: typeof item === 'string' ? item : item?.content ?? '',
  }));
}

/** Drag-drop: right-column correct answers from `data[].correct`. */
function buildDragDropCorrectValue(normalized) {
  const rows = normalized?.data;
  if (!Array.isArray(rows)) return [];
  return rows.map((d, i) => ({
    id: `dd-cor-${i}`,
    content:
      typeof d?.correct === 'string'
        ? d.correct
        : d?.correct != null
        ? String(d.correct)
        : '',
  }));
}

/**
 * Fill-in / gap: per-blank correct strings from `resultAnswers[].answer` when present.
 * @returns {string[] | null}
 */
function buildFillCorrectResultArray(normalized) {
  const ra = normalized?.resultAnswers;
  if (Array.isArray(ra) && ra.length > 0) {
    const fromAnswers = ra.map((x) =>
      x?.answer != null && x?.answer !== ''
        ? String(x.answer)
        : x?.content != null
        ? String(x.content)
        : '',
    );
    if (fromAnswers.some((s) => s !== '')) return fromAnswers;
  }
  if (Array.isArray(normalized?.correctResult)) {
    return normalized.correctResult.map((x) => String(x ?? ''));
  }
  return null;
}

function getFreetextCorrectText(normalized) {
  const v =
    normalized?.correctDataContent ??
    normalized?.correctAnswer ??
    normalized?.modelAnswer ??
    (Array.isArray(normalized?.resultAnswers) && normalized.resultAnswers[0]
      ? normalized.resultAnswers[0]?.answer ??
        normalized.resultAnswers[0]?.content
      : null);
  if (v == null || String(v).trim() === '') return null;
  return String(v);
}

/**
 * @param {any[]} questions
 * @returns {{ total: number; correct: number; incorrect: number; notAnswered: number; pending: number }}
 */
export function getAttemptAnswerSummary(questions) {
  const list = Array.isArray(questions) ? questions : [];
  let correct = 0;
  let incorrect = 0;
  let notAnswered = 0;
  let pending = 0;
  for (const q of list) {
    const s = q?.status;
    if (s === RESULT_STATUS.COMPLETED) correct += 1;
    else if (s === RESULT_STATUS.FAILED) incorrect += 1;
    else if (s === RESULT_STATUS.NOT_ATTEMPT) notAnswered += 1;
    else if (s === RESULT_STATUS.PENDING) pending += 1;
  }
  return {
    total: list.length,
    correct,
    incorrect,
    notAnswered,
    pending,
  };
}

/** @returns {any[]} */
export function getQuestionsFromAttempt(attempt) {
  if (!attempt) return [];
  if (Array.isArray(attempt.questions) && attempt.questions.length > 0) {
    return attempt.questions;
  }
  if (attempt.content != null || attempt.type != null || attempt._id != null) {
    return [attempt];
  }
  return [];
}

/**
 * Question stem — aligned with `renderQuestion` in `ModalDetail.js`.
 * @param {Record<string, any>} item
 * @param {number} index
 */
function renderQuestionStem(item, index) {
  const orderLabel = item?.order != null ? `${item.order})` : `${index + 1})`;

  if (
    item?.type === QUESTION_TYPE.FREETEXT ||
    item?.type === QUESTION_TYPE.MTC ||
    item?.type === QUESTION_TYPE.DRAGDROP ||
    item?.type === QUESTION_TYPE.ORDERING
  ) {
    return (
      <div className="d-flex align-items-center">
        {orderLabel}
        {parseHtml(
          item?.content,
          'fill-in-the-blank fill-overflow-image manual-customtagp customconten_elecment w-100 ml-50',
        )}
      </div>
    );
  }

  if (item?.type === QUESTION_TYPE.FILLTHEGAP) {
    return (
      <div className="freetext-correct-ans">
        <div className="customconten_elecment d-flex align-items-center fill-overflow-image">
          {orderLabel}{' '}
          {parseHtml(item?.contentHTML, 'ml-50 w-100 manual-customtagp')}{' '}
        </div>
      </div>
    );
  }

  if (item?.type === QUESTION_TYPE.FILLINTHEBLANK) {
    return (
      <div className="freetext-correct-ans">
        <div className="d-flex align-items-center customconten_elecment">
          {orderLabel}{' '}
          {parseHtml(
            item?.contentHTML,
            'fill-in-the-blank ml-50 fill-overflow-image w-100 manual-customtagp',
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center">
      {orderLabel}
      {parseHtml(item?.content, 'fill-overflow-image w-100 ml-50')}
    </div>
  );
}

function answerSection(title, children) {
  return (
    <div className="mt-2">
      <span className="view-page-title">{title}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/**
 * Read-only answers — Question Type v2: correct answer + learner answer (non-interactive).
 */
function AdaptiveAnswerReadOnly({ item }) {
  const normalized = normalizeAdaptiveResult(item);
  const readOnly = true;

  const renderCorrectBlock = () => {
    if (normalized?.type === QUESTION_TYPE.DRAGDROP) {
      const v = buildDragDropCorrectValue(normalized);
      if (!v.length && !buildDragDropQuestionsForReadOnly(normalized).length) {
        return <span className="text-muted">—</span>;
      }
      return (
        <DragAndDrop
          readOnly={readOnly}
          questions={buildDragDropQuestionsForReadOnly(normalized)}
          value={v}
          onChange={noop}
        />
      );
    }

    if (normalized?.type === QUESTION_TYPE.ORDERING) {
      const v = buildOrderingCorrectValue(normalized);
      if (!v.length) return <span className="text-muted">—</span>;
      return <Ordering readOnly={readOnly} value={v} onChange={noop} />;
    }

    if (normalized?.type === QUESTION_TYPE.MTC) {
      return (
        <MultipleChoice
          readOnly={readOnly}
          question={normalized.data ?? []}
          value={buildMtcCorrectValue(normalized)}
          onChange={noop}
        />
      );
    }

    if (normalized?.type === QUESTION_TYPE.FREETEXT) {
      const text = getFreetextCorrectText(normalized);
      if (text == null) return <span className="text-muted">—</span>;
      return (
        <FreeText
          readOnly={readOnly}
          value={text}
          onChange={noop}
          explanation={undefined}
          isFullscreenMode={false}
        />
      );
    }

    if (
      normalized?.type === QUESTION_TYPE.FILLTHEGAP ||
      normalized?.type === QUESTION_TYPE.FILLINTHEBLANK
    ) {
      const html = normalized?.modifiedContent ?? normalized?.contentHTML ?? '';
      const correctArr = buildFillCorrectResultArray(normalized);
      if (!correctArr || !correctArr.some((s) => String(s).trim() !== '')) {
        return <span className="text-muted">—</span>;
      }
      const fillProps = {
        readOnly,
        contentHTML: html,
        value: correctArr,
        onChange: noop,
        className:
          'ml-1 text-word-break fill-blank w-100 fill-overflow-image d-flex flex-wrap',
      };
      return normalized?.type === QUESTION_TYPE.FILLINTHEBLANK ? (
        <FillInTheBlank {...fillProps} />
      ) : (
        <FillTheGap {...fillProps} />
      );
    }

    return <span className="text-muted">—</span>;
  };

  const renderLearnerBlock = () => {
    if (normalized?.type === QUESTION_TYPE.DRAGDROP) {
      return (
        <DragAndDrop
          readOnly={readOnly}
          questions={buildDragDropQuestionsForReadOnly(normalized)}
          value={buildDragDropValueForReadOnly(normalized)}
          onChange={noop}
        />
      );
    }

    if (normalized?.type === QUESTION_TYPE.ORDERING) {
      return (
        <Ordering
          readOnly={readOnly}
          value={buildOrderingValueForReadOnly(normalized)}
          onChange={noop}
        />
      );
    }

    if (normalized?.type === QUESTION_TYPE.MTC) {
      return (
        <MultipleChoice
          readOnly={readOnly}
          question={normalized.data ?? []}
          value={Array.isArray(normalized.result) ? normalized.result : []}
          onChange={noop}
        />
      );
    }

    if (normalized?.type === QUESTION_TYPE.FREETEXT) {
      return (
        <FreeText
          readOnly={readOnly}
          value={normalized.dataContent ?? ''}
          onChange={noop}
          explanation={normalized.explanation}
          isFullscreenMode={false}
        />
      );
    }

    if (
      normalized?.type === QUESTION_TYPE.FILLTHEGAP ||
      normalized?.type === QUESTION_TYPE.FILLINTHEBLANK
    ) {
      const html = normalized?.modifiedContent ?? normalized?.contentHTML ?? '';
      const fillProps = {
        readOnly,
        contentHTML: html,
        value: Array.isArray(normalized.result) ? normalized.result : [],
        onChange: noop,
        className:
          'ml-1 text-word-break fill-blank w-100 fill-overflow-image d-flex flex-wrap',
      };
      return normalized?.type === QUESTION_TYPE.FILLINTHEBLANK ? (
        <FillInTheBlank {...fillProps} />
      ) : (
        <FillTheGap {...fillProps} />
      );
    }

    return (
      <p className="text-muted small mb-0">
        Unsupported or incomplete question type for this view.
      </p>
    );
  };

  return (
    <div className="adaptive-answer-readonly">
      {answerSection('Correct answer:', renderCorrectBlock())}
      {answerSection('Learner answer:', renderLearnerBlock())}
    </div>
  );
}

function statusBadge(item) {
  if (item?.status === RESULT_STATUS.COMPLETED) {
    return <Badge color="success">Correct</Badge>;
  }
  if (item?.status === RESULT_STATUS.FAILED) {
    return <Badge color="danger">Incorrect</Badge>;
  }
  if (item?.status === RESULT_STATUS.NOT_ATTEMPT) {
    return <Badge color="secondary">Not Answered</Badge>;
  }
  if (item?.status === RESULT_STATUS.PENDING) {
    return <Badge color="warning">Pending</Badge>;
  }
  return item?.status ? (
    <Badge color={`light-${ADAPTIVE_COLOR_STATUS[item.status] || 'secondary'}`}>
      {String(item.status)}
    </Badge>
  ) : null;
}

function weightLabel(item) {
  const w = item?.weight;
  const uw = item?.userWeight;
  if (w == null && uw == null) return null;
  const earned =
    item?.status === UNIT_STATUS.FAILED
      ? 0
      : item?.status === UNIT_STATUS.COMPLETED
      ? w
      : uw ?? 0;
  return `${earned}/${w ?? '—'}`;
}

/**
 * Layout mirrors `ModalDetail` question blocks: badge, stem, answer region.
 */
export default function AdaptiveAttemptQuestionDetail({ attempt, user }) {
  const questions = getQuestionsFromAttempt(attempt);
  const summary = getAttemptAnswerSummary(questions);

  if (!questions.length) {
    return (
      <p className="text-muted small mb-0">
        No question data for this attempt.
      </p>
    );
  }

  return (
    <div className="adaptive-attempt-question-detail">
      <div className="mb-3">
        {user?.fullName ? (
          <div className="mb-2" style={{ fontSize: 15, fontWeight: 600 }}>
            <strong className="view-page-title">{user.fullName}</strong>
          </div>
        ) : null}
        <div className="d-flex flex-wrap align-items-center gap-1 view-page-title">
          | <strong>Questions: {summary.total}</strong>|{' '}
          <strong>Incorrect: {summary.incorrect}</strong>
        </div>
      </div>

      {questions.map((item, index) => (
        <div key={item._id ?? index} className="adaptive-question-block">
          {index !== 0 ? <hr /> : null}

          <div className="d-flex flex-wrap align-items-center gap-1 mb-2">
            {statusBadge(item)}
          </div>

          {item?.content || item?.contentHTML ? (
            <div className="my-50">
              <div className="d-flex justify-content-between mb-1 align-items-start">
                <strong className="view-page-title d-flex align-items-center">
                  Question:
                </strong>
                {weightLabel(item) != null && (
                  <p className="mb-0 custom-marktest-weight">
                    {weightLabel(item)}
                  </p>
                )}
              </div>
              {renderQuestionStem(item, index)}
            </div>
          ) : null}

          {item?.status !== RESULT_STATUS.NOT_ATTEMPT && (
            <div className="my-2 custom_alignment_modal_test">
              <AdaptiveAnswerReadOnly item={item} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
